// components/CalculoPreciosModal.js
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Modal,
  Form,
  Select,
  DatePicker,
  InputNumber,
  Button,
  Space,
  Table,
  Typography,
  message,
  Row,
  Col,
  Statistic,
  Divider,
  Tag,
  Tooltip,
  Descriptions,
  Alert,
  Tabs,
  Card,
  Spin,
  Collapse,
  Badge,
  Checkbox,
  List,
  Input,
  Popconfirm
} from 'antd';
import {
  CalculatorOutlined,
  DeleteOutlined,
  PlusOutlined,
  CalendarOutlined,
  TeamOutlined,
  EuroOutlined,
  FileTextOutlined,
  HistoryOutlined,
  PrinterOutlined,
  DownloadOutlined,
  HomeOutlined,
  CoffeeOutlined,
  ShoppingOutlined,
  CarryOutOutlined,
  CloseOutlined,
  UserOutlined,
  SmileOutlined,
  RobotOutlined,
  InfoCircleOutlined,
  EditOutlined,
  DollarOutlined,
  TagOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import locale from 'antd/es/date-picker/locale/es_ES';

import { BASE_URL } from '../utils/constants';

const { Text, Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Panel } = Collapse;

// Mapa de iconos y colores por régimen
const regimenConfig = {
  HA: { icon: <HomeOutlined />, color: 'blue', desc: 'Alojamiento' },
  HD: { icon: <CoffeeOutlined />, color: 'green', desc: 'Alojamiento y Desayuno' },
  MP: { icon: <ShoppingOutlined />, color: 'orange', desc: 'Media Pensión' },
  PC: { icon: <CarryOutOutlined />, color: 'purple', desc: 'Pensión Completa' },
  TI: { icon: <CalculatorOutlined />, color: 'red', desc: 'Todo Incluido' }
};

// Tipos de facturación
const tiposFacturacion = [
  { codigo: 'DO', descripcion: 'Doble' },
  { codigo: 'IN', descripcion: 'Individual' },
  { codigo: 'ADE', descripcion: 'Adulto Extra' }
];

const CalculoPreciosModal = forwardRef(({
  visible,
  onClose,
  onSuccess,
  contratoInicial = null,
  fechasIniciales = null,
  habitacionesIniciales = null,
  titulo = "Calculadora de Precios",
  width = 1400,
  allowPrint = true,
  allowExport = true,
  showHistorial = true
}, ref) => {
  // Estados
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [calculando, setCalculando] = useState(false);
  const [contratos, setContratos] = useState([]);
  const [regimenes, setRegimenes] = useState([]);
  const [cargosDisponibles, setCargosDisponibles] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [ultimoContrato, setUltimoContrato] = useState(null);
  const [habitaciones, setHabitaciones] = useState([
    { 
      id: Date.now(), 
      regimen: 'HA', 
      tipoFacturacion: 'DO',
      adultos: 2, 
      ninios: 0, 
      bebes: 0,
      edades: { ninios: [] },
      tasas: true,
      cargos: []
    }
  ]);
  const [modalVisible, setModalVisible] = useState(false);
  const [expandirDetalle, setExpandirDetalle] = useState({});
  const [cargosModalVisible, setCargosModalVisible] = useState(false);
  const [habitacionCargosIndex, setHabitacionCargosIndex] = useState(null);
  const [cargoSeleccionado, setCargoSeleccionado] = useState(null);
  const [cargoPrecio, setCargoPrecio] = useState(0);
  const [cargoCantidad, setCargoCantidad] = useState(1);
  const [cargoDias, setCargoDias] = useState(0);
  const [cargoDiasTodos, setCargoDiasTodos] = useState(true);
  const [editarCargosIndex, setEditarCargosIndex] = useState(null);

  // Exponer métodos al componente padre
  useImperativeHandle(ref, () => ({
    openModal: (config = {}) => {
      if (config.contrato) {
        form.setFieldsValue({ contrato: config.contrato });
      }
      
      if (config.fechas && config.fechas.length === 2) {
        form.setFieldsValue({ fechas: config.fechas });
      }
      
      if (config.habitaciones && config.habitaciones.length > 0) {
        setHabitaciones(config.habitaciones.map((h, index) => ({
          id: Date.now() + index,
          ...h,
          edades: h.edades || { ninios: [] },
          cargos: h.cargos || []
        })));
      }
      
      setModalVisible(true);
    },
    closeModal: () => {
      handleClose();
    },
    setResultado: (data) => {
      setResultado(data);
    }
  }));

  // Sincronizar con prop visible
  useEffect(() => {
    setModalVisible(visible);
  }, [visible]);

  // Cargar datos al abrir
  useEffect(() => {
    if (modalVisible) {
      // fetchContratos();

      fetchContratos().then(() => {
        // Después de cargar contratos, obtener el último
        fetchUltimoContrato();
      });

      fetchRegimenes();
      fetchCargos();
      // fetchUltimoContrato();
    }
  }, [modalVisible]);

  // Establecer valores iniciales
  useEffect(() => {
    if (contratoInicial) {
      form.setFieldsValue({ contrato: contratoInicial });
    }
    if (fechasIniciales) {
      form.setFieldsValue({ fechas: fechasIniciales });
    }
    if (habitacionesIniciales) {
      setHabitaciones(habitacionesIniciales.map((h, index) => ({
        id: Date.now() + index,
        ...h,
        edades: h.edades || { ninios: [] },
        cargos: h.cargos || []
      })));
    }
  }, [contratoInicial, fechasIniciales, habitacionesIniciales]);

  const fetchContratos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/contratos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const contratosData = response.data.data || [];
      setContratos(contratosData);
      return contratosData; // 👈 Retornar para encadenar
    } catch (error) {
      console.error('Error cargando contratos:', error);
      message.error('Error al cargar los contratos');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchRegimenes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/tiposregimen`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegimenes(response.data.data || []);
    } catch (error) {
      console.error('Error cargando regímenes:', error);
    }
  };

  const fetchCargos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/cargos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCargosDisponibles(response.data.data || []);
    } catch (error) {
      console.error('Error cargando cargos:', error);
    }
  };

  const fetchUltimoContrato = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/calculo-precios/ultimo-contrato`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let valorContrato = null;
      const data = response.data.data;
      
      if (data) {
        if (typeof data === 'string') {
          valorContrato = data;
        } else if (data.recordset && data.recordset.length > 0) {
          valorContrato = data.recordset[0].Valor;
        } else if (data.Valor) {
          valorContrato = data.Valor;
        } else {
          valorContrato = data;
        }
      }
      
      
      
      if (valorContrato) {
        setUltimoContrato(valorContrato);
        
        // Usar el estado actual de contratos
        // const contratoExiste = contratos.some(c => c.Codigo === valorContrato);
        // if (contratoExiste && !form.getFieldValue('contrato')) {
          
          form.setFieldsValue({ contrato: valorContrato });
          // console.log(`✅ Contrato "${valorContrato}" cargado correctamente`);
        // }
      }
    } catch (error) {
      console.error('Error obteniendo último contrato:', error);
    }
  };


  const agregarHabitacion = () => {
    const nuevaHabitacion = {
      id: Date.now(),
      regimen: 'HA',
      tipoFacturacion: 'DO',
      adultos: 2,
      ninios: 0,
      bebes: 0,
      edades: { ninios: [] },
      tasas: true,
      cargos: []
    };
    setHabitaciones([...habitaciones, nuevaHabitacion]);
  };

  const eliminarHabitacion = (id) => {
    if (habitaciones.length === 1) {
      message.warning('Debe haber al menos una habitación');
      return;
    }
    setHabitaciones(habitaciones.filter(h => h.id !== id));
  };

  const actualizarHabitacion = (id, campo, valor) => {
    setHabitaciones(habitaciones.map(h => {
      if (h.id === id) {
        const nuevaHabitacion = { ...h, [campo]: valor };
        
        // Si cambia número de niños, ajustar array de edades
        if (campo === 'ninios') {
          const nuevasEdades = [];
          for (let i = 0; i < valor; i++) {
            nuevasEdades.push(h.edades?.ninios?.[i] || null);
          }
          nuevaHabitacion.edades = { ninios: nuevasEdades };
        }
        
        return nuevaHabitacion;
      }
      return h;
    }));
  };

  const actualizarEdadNino = (idHabitacion, indexNino, edad) => {
    setHabitaciones(habitaciones.map(h => {
      if (h.id === idHabitacion) {
        const nuevasEdades = [...(h.edades?.ninios || [])];
        nuevasEdades[indexNino] = edad;
        return {
          ...h,
          edades: { ninios: nuevasEdades }
        };
      }
      return h;
    }));
  };

  // Funciones para cargos
  const abrirModalCargos = (index) => {
    setHabitacionCargosIndex(index);
    setCargoSeleccionado(null);
    setCargoPrecio(0);
    setCargoCantidad(1);
    setCargoDias(0);
    setCargoDiasTodos(true);
    setEditarCargosIndex(null);
    setCargosModalVisible(true);
  };

  const cerrarModalCargos = () => {
    setCargosModalVisible(false);
    setHabitacionCargosIndex(null);
    setCargoSeleccionado(null);
  };

  const seleccionarCargo = (cargo) => {
    setCargoSeleccionado(cargo);
    setCargoPrecio(0);
    setCargoCantidad(1);
    setCargoDias(0);
    setCargoDiasTodos(true);
  };

  const agregarCargo = () => {
    if (!cargoSeleccionado) {
      message.warning('Seleccione un cargo de la lista');
      return;
    }

    // Verificar si el cargo ya está agregado
    const cargosActuales = habitaciones[habitacionCargosIndex]?.cargos || [];
    if (cargosActuales.some(c => c.codigo === cargoSeleccionado.codigo)) {
      message.warning('Este cargo ya está agregado a la habitación');
      return;
    }

    const nuevoCargo = {
      codigo: cargoSeleccionado.codigo,
      descripcion: cargoSeleccionado.descripcion,
      precio: cargoPrecio || 0,
      cantidad: cargoCantidad || 1,
      dias: cargoDiasTodos ? 0 : (cargoDias || 1)
    };

    setHabitaciones(habitaciones.map((h, idx) => {
      if (idx === habitacionCargosIndex) {
        return {
          ...h,
          cargos: [...(h.cargos || []), nuevoCargo]
        };
      }
      return h;
    }));

    cerrarModalCargos();
  };

  const eliminarCargo = (habitacionIdx, cargoIdx) => {
    setHabitaciones(habitaciones.map((h, idx) => {
      if (idx === habitacionIdx) {
        const nuevosCargos = [...(h.cargos || [])];
        nuevosCargos.splice(cargoIdx, 1);
        return {
          ...h,
          cargos: nuevosCargos
        };
      }
      return h;
    }));
  };

  const handleCalcular = async (values) => {
    setCalculando(true);
    
    try {
      const token = localStorage.getItem('token');
      
      if (!values.fechas || values.fechas.length !== 2) {
        message.error('Por favor seleccione fechas de entrada y salida');
        return;
      }

      const fechaEntrada = values.fechas[0].format('YYYY-MM-DD');
      const fechaSalida = values.fechas[1].format('YYYY-MM-DD');

      const habitacionesData = habitaciones.map(h => ({
        regimen: h.regimen,
        tipoFacturacion: h.tipoFacturacion || 'DO',
        adultos: h.adultos,
        ninios: h.ninios,
        bebes: h.bebes,
        edades: h.edades,
        tasas: h.tasas !== undefined ? h.tasas : true,
        cargos: h.cargos || []
      }));

      const payload = {
        contratoCodigo: values.contrato,
        fechaEntrada,
        fechaSalida,
        habitaciones: habitacionesData
      };

      const response = await axios.post(
        `${BASE_URL}/api/calculo-precios/calcular`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResultado(response.data.data);

      
      if (onSuccess) {
        onSuccess(response.data.data);
      }
      
      message.success('Cálculo realizado correctamente');

    } catch (error) {
      console.log('Error en cálculo:', error);
      message.error(error.response?.data?.error || 'Error al realizar el cálculo');
    } finally {
      setCalculando(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setHabitaciones([{ 
      id: Date.now(), 
      regimen: 'HA', 
      tipoFacturacion: 'DO',
      adultos: 2, 
      ninios: 0, 
      bebes: 0,
      edades: { ninios: [] },
      tasas: true,
      cargos: []
    }]);
    setResultado(null);
  };

  const handleClose = () => {
    setModalVisible(false);
    handleReset();
    if (onClose) onClose();
  };


  const handleExportCSV = () => {
    if (!resultado) return;

    let csv = 'Habitación,Fecha,Régimen,Tipo Persona,Nº,Edad,Precio,Tasas,Cargos\n';
    
    resultado.habitaciones.forEach(hab => {
      hab.preciosDiarios.forEach(dia => {
        // Adultos
        dia.detalleAdultos.forEach(adulto => {
          csv += `${hab.habitacion},${dia.fechaFormateada},${hab.regimen},Adulto,${adulto.adulto},,${adulto.precio},${dia.tasasAplicadas || 0},${hab.totalCargos || 0}\n`;
        });
        // Niños
        dia.detalleNinios.forEach(nino => {
          csv += `${hab.habitacion},${dia.fechaFormateada},${hab.regimen},Niño,${nino.nino},${nino.edad},${nino.precio},${dia.tasasAplicadas || 0},${hab.totalCargos || 0}\n`;
        });
        // Bebés
        dia.detalleBebes.forEach(bebe => {
          csv += `${hab.habitacion},${dia.fechaFormateada},${hab.regimen},Bebé,${bebe.bebe},,${bebe.precio},${dia.tasasAplicadas || 0},${hab.totalCargos || 0}\n`;
        });
      });
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `calculo_precios_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleDetalle = (habitacionId, fecha) => {
    const key = `${habitacionId}-${fecha}`;
    setExpandirDetalle(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Columnas para tablas
  const columnasResumen = [
    {
      title: 'Hab.',
      dataIndex: 'habitacion',
      key: 'habitacion',
      width: 60,
      render: (num) => <Tag color="blue">#{num}</Tag>
    },
    {
      title: 'Régimen',
      dataIndex: 'regimen',
      key: 'regimen',
      width: 150,
      render: (reg) => {
        const config = regimenConfig[reg] || { icon: <FileTextOutlined />, color: 'default' };
        return (
          <Tag color={config.color} icon={config.icon}>
            {reg} - {config.desc}
          </Tag>
        );
      }
    },
    {
      title: 'Tipo Fact.',
      dataIndex: 'tipoFacturacion',
      key: 'tipoFacturacion',
      width: 80,
      render: (tipo) => {
        const t = tiposFacturacion.find(t => t.codigo === tipo);
        return t ? t.descripcion : tipo;
      }
    },
    {
      title: 'Ocupación',
      dataIndex: 'totalPersonas',
      key: 'totalPersonas',
      render: (_, record) => (
        <div>
          <div><UserOutlined /> {record.adultos} Adultos</div>
          {record.ninios > 0 && <div><SmileOutlined /> {record.ninios} Niños</div>}
          {record.bebes > 0 && <div><RobotOutlined /> {record.bebes} Bebés</div>}
          <div><TagOutlined /> Tasas: {record.totalTasas?.toFixed(2)}€</div>
          {record.totalCargos > 0 && <div><DollarOutlined /> Cargos: {record.totalCargos?.toFixed(2)}€</div>}
        </div>
      )
    },
    {
      title: 'Total',
      dataIndex: 'totalHabitacion',
      key: 'totalHabitacion',
      align: 'right',
      render: (total) => (
        <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
          {total?.toFixed(2)} €
        </Text>
      )
    }
  ];

  const columnasDiario = [
    {
      title: 'Fecha',
      dataIndex: 'fechaFormateada',
      key: 'fecha',
      width: 50
    },
    {
      title: 'Precios Base',
      key: 'preciosBase',
      width: 50,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag color="blue">PC: {record.precioBase.PC}€</Tag>
          <Tag color="green">MP: {record.precioBase.MP}€</Tag>
          <Tag color="orange">HD: {record.precioBase.HD}€</Tag>
          <Tag color="purple">HA: {record.precioBase.HA}€</Tag>
          <Tag color="red">TI: {record.precioBase.TI}€</Tag>
        </Space>
      )
    },
    {
      title: 'Detalle',
      key: 'detalle',
      width: 50,
      render: (_, record) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {record.detalleAdultos.map(a => (
            <Badge 
              key={`a-${a.adulto}`}
              status="success" 
              text={`Adulto ${a.adulto}: ${a.precio}€`} 
            />
          ))}
          {record.detalleNinios.map(n => (
            <Badge 
              key={`n-${n.nino}`}
              status="warning" 
              text={`Niño ${n.nino} (${n.edad} años): ${n.precio}€`} 
            />
          ))}
          {record.detalleBebes.map(b => (
            <Badge 
              key={`b-${b.bebe}`}
              status="default" 
              text={`Bebé ${b.bebe}: ${b.precio}€`} 
            />
          ))}
          {record.tasasAplicadas > 0 && (
            <Badge status="processing" text={`Tasas: ${record.tasasAplicadas}€`} />
          )}
        </Space>
      )
    },
    {
      title: 'Total Noche',
      dataIndex: 'precioCalculado',
      key: 'precioCalculado',
      align: 'right',
      width: 75,
      render: (precio) => (
        <Text strong style={{ color: '#52c41a', fontSize: '15px' }}>
          {precio?.toFixed(2)} €
        </Text>
      )
    }
  ];

  // Modal de cargos
  const cargosModal = (
    <Modal
      title="Cargos de la Habitación"
      open={cargosModalVisible}
      onCancel={cerrarModalCargos}
      width={900}
      footer={null}
    >
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Cargos Disponibles" size="small">
            <List
              dataSource={cargosDisponibles}
              renderItem={(cargo) => (
                <List.Item
                  onClick={() => seleccionarCargo(cargo)}
                  style={{ 
                    cursor: 'pointer',
                    backgroundColor: cargoSeleccionado?.codigo === cargo.codigo ? '#e6f7ff' : 'transparent'
                  }}
                >
                  <List.Item.Meta
                    title={<Text strong>{cargo.codigo}</Text>}
                    description={cargo.descripcion}
                  />
                </List.Item>
              )}
              style={{ maxHeight: 300, overflow: 'auto' }}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Detalle del Cargo" size="small">
            {cargoSeleccionado ? (
              <div>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Código">{cargoSeleccionado.codigo}</Descriptions.Item>
                  <Descriptions.Item label="Descripción">{cargoSeleccionado.descripcion}</Descriptions.Item>
                </Descriptions>
                <Divider />
                <Form layout="vertical">
                  <Form.Item label="Precio">
                    <InputNumber
                      value={cargoPrecio}
                      onChange={setCargoPrecio}
                      min={0}
                      step={0.01}
                      style={{ width: '100%' }}
                      prefix="€"
                    />
                  </Form.Item>
                  <Form.Item label="Cantidad">
                    <InputNumber
                      value={cargoCantidad}
                      onChange={setCargoCantidad}
                      min={1}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <Form.Item label="Días">
                    <Checkbox
                      checked={cargoDiasTodos}
                      onChange={(e) => setCargoDiasTodos(e.target.checked)}
                    >
                      Todos los días
                    </Checkbox>
                    {!cargoDiasTodos && (
                      <InputNumber
                        value={cargoDias}
                        onChange={setCargoDias}
                        min={1}
                        style={{ width: '100%', marginTop: 8 }}
                      />
                    )}
                  </Form.Item>
                  <Button
                    type="primary"
                    onClick={agregarCargo}
                    block
                    icon={<PlusOutlined />}
                  >
                    Agregar Cargo
                  </Button>
                </Form>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <Text type="secondary">Seleccione un cargo de la lista</Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>
      <Divider />
      <Card title="Cargos Seleccionados" size="small">
        <Table
          dataSource={habitaciones[habitacionCargosIndex]?.cargos || []}
          rowKey="codigo"
          columns={[
            { title: 'Código', dataIndex: 'codigo' },
            { title: 'Descripción', dataIndex: 'descripcion' },
            { title: 'Precio', dataIndex: 'precio', render: (v) => `${v}€` },
            { title: 'Cantidad', dataIndex: 'cantidad' },
            { title: 'Días', dataIndex: 'dias', render: (v) => v === 0 ? 'Todos' : v },
            {
              title: 'Acciones',
              key: 'acciones',
              render: (_, __, idx) => (
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => eliminarCargo(habitacionCargosIndex, idx)}
                />
              )
            }
          ]}
          pagination={false}
          size="small"
        />
      </Card>
    </Modal>
  );

// En el componente, fuera del return
const generarHTMLImpresion = () => {
  if (!resultado) return '';

  const { contrato, estancia, habitaciones, totales } = resultado;

  let html = `
    <div style="padding: 20px; font-family: Arial, sans-serif; max-width: 1200px; margin: 0 auto;">
      <!-- Encabezado -->
      <div style="text-align: center; margin-bottom: 20px; border-bottom: 2px solid #1890ff; padding-bottom: 10px;">
        <h1 style="margin: 0; color: #1890ff;">Valoración estancia</h1>
        <h3 style="margin: 5px 0; color: #666;">${contrato.codigo} - ${contrato.descripcion}</h3>
        <p style="margin: 5px 0; color: #999;">
          ${estancia.fechaEntradaFormateada} → ${estancia.fechaSalidaFormateada} (${estancia.noches} noches)
        </p>
      </div>

      <!-- Totales -->
      <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 20px; background: #f5f5f5; padding: 15px; border-radius: 8px;">
        <div style="text-align: center;">
          <div style="font-size: 12px; color: #666;">Total Estancia</div>
          <div style="font-size: 20px; font-weight: bold; color: #1890ff;">${totales.totalEstancia?.toFixed(2)} ${contrato.moneda}</div>
        </div>
        <div style="text-align: center;">
          <div style="font-size: 12px; color: #666;">Total Tasas</div>
          <div style="font-size: 20px; font-weight: bold; color: #faad14;">${totales.totalTasas?.toFixed(2)} ${contrato.moneda}</div>
        </div>
        <div style="text-align: center;">
          <div style="font-size: 12px; color: #666;">Total Cargos</div>
          <div style="font-size: 20px; font-weight: bold; color: #722ed1;">${totales.totalCargos?.toFixed(2)} ${contrato.moneda}</div>
        </div>
        <div style="text-align: center;">
          <div style="font-size: 12px; color: #666;">Total General</div>
          <div style="font-size: 24px; font-weight: bold; color: #52c41a;">${totales.totalGeneral?.toFixed(2)} ${contrato.moneda}</div>
        </div>
      </div>

      <!-- Información adicional -->
      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 20px;">
        <div><strong>Personas:</strong> ${totales.totalPersonas} (${totales.totalAdultos} A, ${totales.totalNinios} N, ${totales.totalBebes} B)</div>
        <div><strong>Habitaciones:</strong> ${totales.totalHabitaciones}</div>
        <div><strong>Moneda:</strong> ${contrato.moneda}</div>
      </div>

      <!-- Resumen por habitación -->
      <h3 style="background: #1890ff; color: white; padding: 8px 12px; border-radius: 4px; margin-bottom: 10px;">Resumen por Habitación</h3>
      <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 20px;">
        <thead>
          <tr style="background: #f0f0f0;">
            <th style="padding: 6px 8px; border: 1px solid #ddd; text-align: left;">Hab.</th>
            <th style="padding: 6px 8px; border: 1px solid #ddd; text-align: left;">Régimen</th>
            <th style="padding: 6px 8px; border: 1px solid #ddd; text-align: left;">Tipo Fact.</th>
            <th style="padding: 6px 8px; border: 1px solid #ddd; text-align: left;">Ocupación</th>
            <th style="padding: 6px 8px; border: 1px solid #ddd; text-align: right;">Tasas</th>
            <th style="padding: 6px 8px; border: 1px solid #ddd; text-align: right;">Cargos</th>
            <th style="padding: 6px 8px; border: 1px solid #ddd; text-align: right;">Total</th>
          </tr>
        </thead>
        <tbody>
  `;

  habitaciones.forEach(hab => {
    html += `
      <tr>
        <td style="padding: 6px 8px; border: 1px solid #ddd;">#${hab.habitacion}</td>
        <td style="padding: 6px 8px; border: 1px solid #ddd;">${hab.regimen}</td>
        <td style="padding: 6px 8px; border: 1px solid #ddd;">${hab.tipoFacturacion || 'DO'}</td>
        <td style="padding: 6px 8px; border: 1px solid #ddd;">
          ${hab.adultos}A ${hab.ninios > 0 ? `${hab.ninios}N` : ''} ${hab.bebes > 0 ? `${hab.bebes}B` : ''}
        </td>
        <td style="padding: 6px 8px; border: 1px solid #ddd; text-align: right;">${hab.totalTasas?.toFixed(2)}€</td>
        <td style="padding: 6px 8px; border: 1px solid #ddd; text-align: right;">${hab.totalCargos?.toFixed(2)}€</td>
        <td style="padding: 6px 8px; border: 1px solid #ddd; text-align: right; font-weight: bold;">${hab.totalHabitacion?.toFixed(2)}€</td>
      </tr>
    `;
  });

  html += `
        </tbody>
      </table>

      <!-- Detalle diario por habitación -->
  `;

  habitaciones.forEach(hab => {
    html += `
      <h4 style="background: #e6f7ff; padding: 6px 12px; border-radius: 4px; border-left: 4px solid #1890ff; margin: 15px 0 8px 0;">
        Habitación #${hab.habitacion} - ${hab.regimen}
      </h4>
      <table style="width: 100%; border-collapse: collapse; font-size: 10px; margin-bottom: 15px;">
        <thead>
          <tr style="background: #fafafa;">
            <th style="padding: 4px 6px; border: 1px solid #ddd; text-align: left;">Fecha</th>
            <th style="padding: 4px 6px; border: 1px solid #ddd; text-align: left;">Adultos</th>
            <th style="padding: 4px 6px; border: 1px solid #ddd; text-align: left;">Niños</th>
            <th style="padding: 4px 6px; border: 1px solid #ddd; text-align: left;">Bebés</th>
            <th style="padding: 4px 6px; border: 1px solid #ddd; text-align: right;">Tasas</th>
            <th style="padding: 4px 6px; border: 1px solid #ddd; text-align: right;">Total Noche</th>
          </tr>
        </thead>
        <tbody>
    `;

    hab.preciosDiarios.forEach(dia => {
      const adultosText = dia.detalleAdultos.map(a => `${a.adulto}: ${a.precio}€`).join(', ');
      const niniosText = dia.detalleNinios.map(n => `${n.nino}(${n.edad}): ${n.precio}€`).join(', ') || '-';
      const bebesText = dia.detalleBebes.map(b => `${b.bebe}: ${b.precio}€`).join(', ') || '-';
      
      html += `
        <tr>
          <td style="padding: 4px 6px; border: 1px solid #ddd;">${dia.fechaFormateada}</td>
          <td style="padding: 4px 6px; border: 1px solid #ddd;">${adultosText}</td>
          <td style="padding: 4px 6px; border: 1px solid #ddd;">${niniosText}</td>
          <td style="padding: 4px 6px; border: 1px solid #ddd;">${bebesText}</td>
          <td style="padding: 4px 6px; border: 1px solid #ddd; text-align: right;">${dia.tasasAplicadas > 0 ? `${dia.tasasAplicadas.toFixed(2)}€` : '-'}</td>
          <td style="padding: 4px 6px; border: 1px solid #ddd; text-align: right; font-weight: bold;">${dia.precioCalculado?.toFixed(2)}€</td>
        </tr>
      `;
    });

    html += `
          <tr style="background: #f0f0f0; font-weight: bold;">
            <td colspan="5" style="padding: 6px 8px; border: 1px solid #ddd; text-align: right;">Total Habitación #${hab.habitacion}</td>
            <td style="padding: 6px 8px; border: 1px solid #ddd; text-align: right; color: #1890ff;">${hab.totalHabitacion?.toFixed(2)}€</td>
          </tr>
        </tbody>
      </table>
    `;
  });

  // Desglose por régimen
  html += `
    <h3 style="background: #f0f0f0; padding: 8px 12px; border-radius: 4px; margin: 20px 0 10px 0;">Desglose por Régimen</h3>
    <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
      <thead>
        <tr style="background: #f0f0f0;">
          <th style="padding: 6px 8px; border: 1px solid #ddd; text-align: left;">Régimen</th>
          <th style="padding: 6px 8px; border: 1px solid #ddd; text-align: left;">Descripción</th>
          <th style="padding: 6px 8px; border: 1px solid #ddd; text-align: right;">Total</th>
        </tr>
      </thead>
      <tbody>
  `;

  Object.entries(totales.totalPorRegimen).forEach(([reg, total]) => {
    if (total > 0) {
      html += `
        <tr>
          <td style="padding: 6px 8px; border: 1px solid #ddd;">${reg}</td>
          <td style="padding: 6px 8px; border: 1px solid #ddd;">${regimenConfig[reg]?.desc || reg}</td>
          <td style="padding: 6px 8px; border: 1px solid #ddd; text-align: right;">${total.toFixed(2)}€</td>
        </tr>
      `;
    }
  });

  html += `
      </tbody>
    </table>

    <!-- Pie de página -->
    <div style="margin-top: 30px; padding-top: 10px; border-top: 1px solid #ddd; text-align: center; font-size: 10px; color: #999;">
      <p>Documento generado desde la Calculadora de Precios</p>
      <p>Fecha: ${new Date().toLocaleString('es-ES')}</p>
    </div>
  </div>
  `;

  return html;
};

// Reemplaza handlePrint
const handlePrint = () => {
  if (!resultado) {
    message.warning('No hay resultados para imprimir');
    return;
  }

  const htmlContent = generarHTMLImpresion();
  
  const printWindow = window.open('', '_blank', 'width=1024,height=768');
  if (!printWindow) {
    message.error('No se pudo abrir la ventana de impresión. Permita ventanas emergentes.');
    return;
  }

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Calculadora de Precios - ${resultado.contrato.codigo}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
          table { page-break-inside: avoid; }
          tr { page-break-inside: avoid; }
          @media print {
            body { padding: 10px; font-size: 10px; }
            h1 { font-size: 18px; }
            h3 { font-size: 14px; }
            h4 { font-size: 12px; }
            table { font-size: 9px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        ${htmlContent}
      </body>
    </html>
  `);
  
  printWindow.document.close();
  
  setTimeout(() => {
    printWindow.focus();
    printWindow.print();
  }, 600);
};

  return (
    <>
      <Modal
        title={
          <Space>
            <CalculatorOutlined style={{ color: '#1890ff' }} />
            <span>{titulo}</span>
          </Space>
        }
        open={modalVisible}
        onCancel={handleClose}
        width={width}
        footer={null}
        closeIcon={<CloseOutlined />}
        style={{ top: 20 }}
        bodyStyle={{ 
          maxHeight: 'calc(100vh - 100px)', 
          overflowY: 'auto',
          padding: '16px'
        }}
      >
        <Spin spinning={loading || calculando}>
          <Row gutter={[16, 16]}>
            {/* Columna izquierda - Formulario */}
            <Col xs={24} lg={8}>
              <Card 
                size="small"
                title={
                  <Space>
                    <CalculatorOutlined />
                    <span>Datos de la estancia</span>
                  </Space>
                }
                style={{ borderRadius: '8px' }}
              >
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleCalcular}
                >
                  <Form.Item
                    name="contrato"
                    label="Contrato"
                    rules={[{ required: true, message: 'Seleccione un contrato' }]}
                  >
                    <Select
                      showSearch
                      placeholder="Buscar contrato..."
                      optionFilterProp="children"
                      size="middle"
                    >
                      {contratos.map(contrato => (
                        <Option key={contrato.Codigo} value={contrato.Codigo}>
                          {contrato.Codigo} - {contrato.Descripcion}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="fechas"
                    label="Fechas"
                    rules={[{ required: true, message: 'Seleccione las fechas' }]}
                  >
                    <RangePicker
                      locale={locale}
                      format="DD/MM/YYYY"
                      style={{ width: '100%' }}
                      size="middle"
                      disabledDate={(current) => current && current < dayjs().startOf('day')}
                    />
                  </Form.Item>

                  <Divider style={{ margin: '12px 0' }}>Habitaciones</Divider>

                  {habitaciones.map((habitacion, idx) => (
                    <Card
                      key={habitacion.id}
                      size="small"
                      title={`Habitación ${idx + 1}`}
                      extra={
                        <Space>
                          <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => abrirModalCargos(idx)}
                            size="small"
                          >
                            Cargos
                          </Button>
                          <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => eliminarHabitacion(habitacion.id)}
                            size="small"
                          />
                        </Space>
                      }
                      style={{ marginBottom: 12, backgroundColor: '#f9f9f9' }}
                    >
                      <Form.Item label="Régimen" style={{ marginBottom: 8 }}>
                        <Select
                          value={habitacion.regimen}
                          onChange={(val) => actualizarHabitacion(habitacion.id, 'regimen', val)}
                          size="small"
                        >
                          {regimenes.map(reg => (
                            <Option key={reg.Codigo} value={reg.Codigo}>
                              {reg.Codigo} - {reg.DescripcionCorta || reg.Descripcion}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item label="Tipo Facturación" style={{ marginBottom: 8 }}>
                        <Select
                          value={habitacion.tipoFacturacion || 'DO'}
                          onChange={(val) => actualizarHabitacion(habitacion.id, 'tipoFacturacion', val)}
                          size="small"
                        >
                          {tiposFacturacion.map(t => (
                            <Option key={t.codigo} value={t.codigo}>
                              {t.descripcion}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Row gutter={4}>
                        <Col span={8}>
                          <Form.Item label="Adultos" style={{ marginBottom: 4 }}>
                            <InputNumber
                              min={1}
                              max={4}
                              value={habitacion.adultos}
                              onChange={(val) => actualizarHabitacion(habitacion.id, 'adultos', val)}
                              size="small"
                              style={{ width: '100%' }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label="Niños" style={{ marginBottom: 4 }}>
                            <InputNumber
                              min={0}
                              max={3}
                              value={habitacion.ninios}
                              onChange={(val) => actualizarHabitacion(habitacion.id, 'ninios', val)}
                              size="small"
                              style={{ width: '100%' }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label="Bebés" style={{ marginBottom: 4 }}>
                            <InputNumber
                              min={0}
                              max={2}
                              value={habitacion.bebes}
                              onChange={(val) => actualizarHabitacion(habitacion.id, 'bebes', val)}
                              size="small"
                              style={{ width: '100%' }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item label="Calcular Tasas" style={{ marginBottom: 8 }}>
                        <Checkbox
                          checked={habitacion.tasas !== undefined ? habitacion.tasas : true}
                          onChange={(e) => actualizarHabitacion(habitacion.id, 'tasas', e.target.checked)}
                        />
                      </Form.Item>

                      {/* Edades de niños */}
                      {habitacion.ninios > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>Edades de los niños:</Text>
                          <Row gutter={4} style={{ marginTop: 4 }}>
                            {[...Array(habitacion.ninios)].map((_, idx) => (
                              <Col span={8} key={idx}>
                                <InputNumber
                                  placeholder={`Niño ${idx + 1}`}
                                  min={0}
                                  max={17}
                                  value={habitacion.edades?.ninios?.[idx]}
                                  onChange={(val) => actualizarEdadNino(habitacion.id, idx, val)}
                                  size="small"
                                  style={{ width: '100%' }}
                                />
                              </Col>
                            ))}
                          </Row>
                          <Text type="secondary" style={{ fontSize: '10px' }}>
                            <InfoCircleOutlined /> Dejar vacío = primer rango
                          </Text>
                        </div>
                      )}

                      {/* Mostrar cargos asignados */}
                      {habitacion.cargos && habitacion.cargos.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <Tag color="green" icon={<DollarOutlined />}>
                            {habitacion.cargos.length} cargo(s) asignado(s)
                          </Tag>
                        </div>
                      )}
                    </Card>
                  ))}

                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={agregarHabitacion}
                    block
                    size="small"
                    style={{ marginBottom: 16 }}
                  >
                    Añadir habitación
                  </Button>

                  <Space style={{ width: '100%' }} direction="vertical">
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<CalculatorOutlined />}
                      block
                      loading={calculando}
                    >
                      Calcular
                    </Button>
                    
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={handleReset}
                      block
                      size="small"
                    >
                      Limpiar
                    </Button>
                  </Space>

                  {showHistorial && ultimoContrato && (
                    <Alert
                      style={{ marginTop: 12 }}
                      message={`Último contrato: ${ultimoContrato}`}
                      type="info"
                      showIcon
                      icon={<HistoryOutlined />}
                    />
                  )}
                </Form>
              </Card>
            </Col>

            {/* Columna derecha - Resultados */}
            <Col xs={24} lg={16}>
              {resultado ? (
                <div id="resultado-print">
                  <Card
                    size="small"
                    title={
                      <Space>
                        <FileTextOutlined />
                        <span>Resultado del Cálculo</span>
                      </Space>
                    }
                    extra={
                      <Space size={4}>
                        {allowPrint && (
                          <Tooltip title="Imprimir">
                            <Button 
                              icon={<PrinterOutlined />} 
                              onClick={handlePrint}
                              size="small"
                            />
                          </Tooltip>
                        )}
                        {allowExport && (
                          <Tooltip title="Exportar CSV">
                            <Button 
                              icon={<DownloadOutlined />} 
                              onClick={handleExportCSV}
                              size="small"
                            />
                          </Tooltip>
                        )}
                      </Space>
                    }
                  >
                    {/* Resumen rápido */}
                    <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
                      <Col span={24}>
                        <Tag color="blue" style={{ width: '100%', padding: '4px', textAlign: 'center' }}>
                          <FileTextOutlined /> {resultado.contrato.codigo} - {resultado.contrato.descripcion}
                        </Tag>
                      </Col>
                      
                      <Col xs={24} sm={6}>
                        <Statistic
                          title="Total Estancia"
                          value={resultado.totales.totalEstancia}
                          precision={2}
                          suffix={resultado.contrato.moneda}
                          prefix={<EuroOutlined />}
                          valueStyle={{ color: '#1890ff', fontSize: '18px' }}
                        />
                      </Col>
                      
                      <Col xs={24} sm={6}>
                        <Statistic
                          title="Total Tasas"
                          value={resultado.totales.totalTasas}
                          precision={2}
                          suffix={resultado.contrato.moneda}
                          prefix={<EuroOutlined />}
                          valueStyle={{ color: '#faad14', fontSize: '18px' }}
                        />
                      </Col>
                      
                      <Col xs={24} sm={6}>
                        <Statistic
                          title="Total Cargos"
                          value={resultado.totales.totalCargos}
                          precision={2}
                          suffix={resultado.contrato.moneda}
                          prefix={<EuroOutlined />}
                          valueStyle={{ color: '#722ed1', fontSize: '18px' }}
                        />
                      </Col>
                      
                      <Col xs={24} sm={6}>
                        <Statistic
                          title="Total General"
                          value={resultado.totales.totalGeneral}
                          precision={2}
                          suffix={resultado.contrato.moneda}
                          prefix={<EuroOutlined />}
                          valueStyle={{ color: '#52c41a', fontSize: '20px', fontWeight: 'bold' }}
                        />
                      </Col>
                    </Row>

                    <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
                      <Col xs={24} sm={6}>
                        <Statistic
                          title="Noches"
                          value={resultado.estancia.noches}
                          suffix="noches"
                          prefix={<CalendarOutlined />}
                          valueStyle={{ fontSize: '18px' }}
                        />
                      </Col>
                      <Col xs={24} sm={6}>
                        <Statistic
                          title="Personas"
                          value={resultado.totales.totalPersonas}
                          suffix={` (${resultado.totales.totalAdultos} A, ${resultado.totales.totalNinios} N, ${resultado.totales.totalBebes} B)`}
                          prefix={<TeamOutlined />}
                          valueStyle={{ fontSize: '16px' }}
                        />
                      </Col>
                      <Col xs={24} sm={6}>
                        <Statistic
                          title="Habitaciones"
                          value={resultado.totales.totalHabitaciones}
                          prefix={<HomeOutlined />}
                          valueStyle={{ fontSize: '18px' }}
                        />
                      </Col>
                    </Row>

                    <Descriptions size="small" column={2} style={{ marginBottom: 16 }}>
                      <Descriptions.Item label="Entrada">
                        {resultado.estancia.fechaEntradaFormateada}
                      </Descriptions.Item>
                      <Descriptions.Item label="Salida">
                        {resultado.estancia.fechaSalidaFormateada}
                      </Descriptions.Item>
                    </Descriptions>

                    {/* Resumen por habitaciones */}
                    <Table
                      columns={columnasResumen}
                      dataSource={resultado.habitaciones}
                      pagination={false}
                      rowKey="habitacion"
                      size="small"
                      style={{ marginBottom: 16 }}
                    />

                    {/* Detalle diario por habitación */}
                    <Tabs defaultActiveKey="1" size="small" type="card">
                      {resultado.habitaciones.map((hab) => (
                        <TabPane 
                          tab={
                            <span>
                              <HomeOutlined />
                              Hab {hab.habitacion}
                            </span>
                          } 
                          key={hab.habitacion.toString()}
                        >
                          <Table
                            columns={columnasDiario}
                            dataSource={hab.preciosDiarios}
                            pagination={false}
                            rowKey="fecha"
                            size="small"
                            scroll={{ x: 800 }}
                            summary={() => (
                              <Table.Summary fixed>
                                <Table.Summary.Row>
                                  <Table.Summary.Cell index={0} colSpan={3}>
                                    <Text strong>Total Habitación {hab.habitacion}</Text>
                                    {hab.totalTasas > 0 && (
                                      <Text type="secondary" style={{ marginLeft: 8 }}>
                                        (Tasas: {hab.totalTasas.toFixed(2)}€)
                                      </Text>
                                    )}
                                    {hab.totalCargos > 0 && (
                                      <Text type="secondary" style={{ marginLeft: 8 }}>
                                        (Cargos: {hab.totalCargos.toFixed(2)}€)
                                      </Text>
                                    )}
                                  </Table.Summary.Cell>
                                  <Table.Summary.Cell index={1} align="right">
                                    <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
                                      {hab.totalHabitacion.toFixed(2)} €
                                    </Text>
                                  </Table.Summary.Cell>
                                </Table.Summary.Row>
                              </Table.Summary>
                            )}
                          />
                        </TabPane>
                      ))}
                    </Tabs>

                    {/* Desglose por régimen */}
                    <Collapse 
                      size="small" 
                      style={{ marginTop: 16, backgroundColor: '#fafafa' }}
                    >
                      <Panel header="Desglose por régimen" key="1">
                        <Row gutter={[16, 16]}>
                          {Object.entries(resultado.totales.totalPorRegimen).map(([reg, total]) => (
                            total > 0 && (
                              <Col xs={24} sm={12} md={8} key={reg}>
                                <Card size="small">
                                  <Statistic
                                    title={
                                      <Space>
                                        {regimenConfig[reg]?.icon}
                                        <span>{reg} - {regimenConfig[reg]?.desc}</span>
                                      </Space>
                                    }
                                    value={total}
                                    precision={2}
                                    suffix="€"
                                    valueStyle={{ color: regimenConfig[reg]?.color }}
                                  />
                                </Card>
                              </Col>
                            )
                          ))}
                        </Row>
                      </Panel>
                    </Collapse>
                  </Card>
                </div>
              ) : (
                <Card
                  style={{ 
                    height: '100%', 
                    minHeight: 500,
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: '#fafafa'
                  }}
                >
                  <Space direction="vertical" align="center">
                    <CalculatorOutlined style={{ fontSize: 48, color: '#bfbfbf' }} />
                    <Title level={4} style={{ color: '#bfbfbf', margin: 0 }}>
                      Complete los datos para calcular
                    </Title>
                    <Text type="secondary">
                      Seleccione contrato, fechas y configure las habitaciones
                    </Text>
                  </Space>
                </Card>
              )}
            </Col>
          </Row>
        </Spin>

        {/* Estilos para impresión */}
        <style jsx global>{`
          @media print {
            body * {
              visibility: hidden;
            }
            #resultado-print, #resultado-print * {
              visibility: visible;
            }
            #resultado-print {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              padding: 20px;
            }
            .ant-card-extra, .ant-tabs-nav, .ant-collapse-header, .ant-btn {
              display: none !important;
            }
            .ant-table {
              font-size: 10px;
            }
          }
        `}</style>
      </Modal>

      {/* Modal de Cargos */}
      {cargosModal}
    </>
  );
});

export default CalculoPreciosModal;